import { buildZip, type ZipEntry } from './zip';
import type { AdminUploadFile, AdminUploadListItem } from './uploadsAdminApi';

// One request per file, so hold the district's server to a few at a time.
const DEFAULT_CONCURRENCY = 4;

type FetchFile = (
  partition: string,
  schoolYear: string,
  version: number,
) => Promise<AdminUploadFile>;

type CollectOptions = {
  concurrency?: number;
  onProgress?: (done: number, total: number) => void;
};

export type DistrictArchive = {
  entries: ZipEntry[];
  failures: string[];
};

function safeSegment(value: string): string {
  return value.replace(/[/\\]/g, '-').trim();
}

function withSuffix(name: string, suffix: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return `${name}${suffix}`;
  return `${name.slice(0, dot)}${suffix}${name.slice(dot)}`;
}

function pathFor(row: AdminUploadListItem, name: string): string {
  const folder = safeSegment(row.schoolYear ?? '');
  return folder ? `${folder}/${name}` : name;
}

/**
 * A stable, collision-free path for each row, in the order given. Names only carry
 * their version when a plainer name would be ambiguous.
 */
export function zipEntryNames(rows: AdminUploadListItem[]): string[] {
  const plainCounts = new Map<string, number>();
  for (const row of rows) {
    const key = pathFor(row, safeSegment(row.name) || 'upload.csv');
    plainCounts.set(key, (plainCounts.get(key) ?? 0) + 1);
  }

  const used = new Set<string>();
  return rows.map((row) => {
    const base = safeSegment(row.name) || 'upload.csv';
    const plain = pathFor(row, base);
    let candidate =
      (plainCounts.get(plain) ?? 0) > 1 ? pathFor(row, withSuffix(base, ` (v${row.version})`)) : plain;
    if (used.has(candidate)) {
      const versioned = candidate;
      let counter = 2;
      while (used.has(candidate)) {
        candidate = withSuffix(versioned, ` (${counter})`);
        counter += 1;
      }
    }
    used.add(candidate);
    return candidate;
  });
}

export function archiveFileName(label: string): string {
  const safe = label.replace(/[^\w \-.]/g, '-').replace(/\s+/g, ' ').trim();
  return safe ? `${safe} uploads.zip` : 'uploads.zip';
}

/**
 * Fetches every listed file — a few at a time — and returns them as zip entries.
 * Files that fail to load are reported rather than sinking the whole archive.
 */
export async function collectDistrictArchive(
  partition: string,
  rows: AdminUploadListItem[],
  fetchFile: FetchFile,
  { concurrency = DEFAULT_CONCURRENCY, onProgress }: CollectOptions = {},
): Promise<DistrictArchive> {
  const names = zipEntryNames(rows);
  const texts: (string | null)[] = new Array(rows.length).fill(null);
  const failures: string[] = [];
  let next = 0;
  let done = 0;

  async function worker() {
    while (next < rows.length) {
      const index = next;
      next += 1;
      const row = rows[index];
      if (!row) continue;
      try {
        const file = await fetchFile(partition, row.schoolYear, row.version);
        texts[index] = file.csv;
      } catch {
        failures.push(row.name);
      }
      done += 1;
      onProgress?.(done, rows.length);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, rows.length) }, () => worker()),
  );

  const entries: ZipEntry[] = [];
  texts.forEach((text, index) => {
    if (text !== null) entries.push({ name: names[index] ?? 'upload.csv', text });
  });
  return { entries, failures };
}

export function saveZip(entries: ZipEntry[], fileName: string) {
  const bytes = buildZip(entries);
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
